import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { SETTINGS } from "../config/constants";

const MODULES_KEY = ["school-modules"];

export const MODULE_LINKS = {
  results: "assessments",
  assessments: "results",
};

export const MODULE_META = [
  {
    key: "fees",
    label: "Fees",
    hint: "Parent vouchers and the teacher class fee list",
    audience: "Parents and teachers",
  },
  {
    key: "gallery",
    label: "Gallery",
    hint: "School photo albums in both apps",
    audience: "Parents and teachers",
  },
  {
    key: "ads",
    label: "Advertisements",
    hint: "Home banner ads in both apps",
    audience: "Parents and teachers",
  },
  {
    key: "chat",
    label: "Chat",
    hint: "Parent and teacher messaging",
    audience: "Parents and teachers",
  },
  {
    key: "results",
    label: "Results",
    hint: "Exam marks for parents and teachers",
    audience: "Parents and teachers",
    linked: "Tests",
  },
  {
    key: "assessments",
    label: "Tests",
    hint: "Class tests and quizzes",
    audience: "Parents and teachers",
    linked: "Results",
  },
  {
    key: "register",
    label: "Class register",
    hint: "Today's attendance list for the teacher's class",
    audience: "Teachers",
  },
  {
    key: "notifications",
    label: "Notifications",
    hint: "Teacher notification inbox",
    audience: "Teachers",
  },
];

const DEFAULT_MODULES = {
  fees: true,
  gallery: true,
  ads: true,
  results: true,
  chat: true,
  assessments: true,
  register: true,
  notifications: true,
};

export const useGetModules = () => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: MODULES_KEY,
    queryFn: async () => {
      const response = await Get(SETTINGS.getModules, token);
      return response?.data?.modules || response?.modules || DEFAULT_MODULES;
    },
    enabled: !!token,
  });
};

const RULES_KEY = ["teacher-attendance-rules"];
const LEAVES_KEY = ["pending-teacher-leaves"];
const DUTY_KEY = ["teacher-duty-days"];

export const useGetTeacherRules = () => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: RULES_KEY,
    queryFn: async () => {
      const response = await Get(SETTINGS.getTeacherRules, token);
      return response?.data?.rules || response?.rules;
    },
    enabled: !!token,
  });
};

export const useUpdateTeacherRules = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (body) => {
      const response = await Post(SETTINGS.updateTeacherRules, body, token);
      if (response?.status === false || response?.data?.status === false) {
        throw new Error(response?.message || response?.data?.message || "Could not save");
      }
      return response?.data?.rules || response?.data?.data?.rules;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_KEY });
    },
  });
};

export const useTeacherDutyDays = () => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: DUTY_KEY,
    queryFn: async () => {
      const response = await Get(SETTINGS.teacherDutyDays, token);
      return response?.data?.days || response?.days || [];
    },
    enabled: !!token,
  });
};

export const useSaveTeacherDutyDay = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (body) => {
      const response = await Post(SETTINGS.teacherDutyDays, body, token);
      if (response?.status === false || response?.data?.status === false) {
        throw new Error(response?.message || response?.data?.message || "Could not save special day");
      }
      return response;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DUTY_KEY }),
  });
};

export const useDeleteTeacherDutyDay = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Post(SETTINGS.deleteTeacherDutyDay + id, {}, token);
      if (response?.status === false || response?.data?.status === false) {
        throw new Error(response?.message || response?.data?.message || "Could not remove special day");
      }
      return response;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: DUTY_KEY }),
  });
};

export const usePendingTeacherLeaves = () => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: LEAVES_KEY,
    queryFn: async () => {
      const response = await Get(SETTINGS.pendingTeacherLeaves, token);
      return response?.data?.leaves || response?.leaves || [];
    },
    enabled: !!token,
  });
};

export const useReviewTeacherLeave = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, decision }) => {
      const response = await Post(SETTINGS.reviewTeacherLeave + id, { decision }, token);
      if (response?.status === false || response?.data?.status === false) {
        throw new Error(response?.message || response?.data?.message || "Could not update leave");
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LEAVES_KEY });
    },
  });
};

export const useUpdateModules = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (modules) => {
      const response = await Post(SETTINGS.updateModules, { modules }, token);
      if (response?.data?.status !== false) return response.data?.data?.modules || modules;
      throw new Error(response?.data?.message || "Failed to save modules");
    },
    onSuccess: (modules) => {
      queryClient.setQueryData(MODULES_KEY, modules);
      queryClient.invalidateQueries({ queryKey: MODULES_KEY });
    },
  });
};

const APP_INFO_KEY = ["app-info"];

export const useGetAppInfo = () => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: APP_INFO_KEY,
    queryFn: async () => {
      const response = await Get(SETTINGS.getAppInfo, token);
      return response?.data?.appInfo || {
        teacherVersion: "",
        parentVersion: "",
        privacyPolicy: "",
        termsOfUse: "",
      };
    },
    enabled: !!token,
  });
};

export const useUpdateAppInfo = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (body) => {
      const response = await Post(SETTINGS.updateAppInfo, body, token);
      if (response?.data?.status === false) {
        throw new Error(response?.data?.message || "Could not save");
      }
      return response?.data?.data?.appInfo || body;
    },
    onSuccess: (appInfo) => {
      queryClient.setQueryData(APP_INFO_KEY, appInfo);
    },
  });
};

const FEE_POLICY_KEY = ["fee-policy"];

export const useGetFeePolicy = () => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: FEE_POLICY_KEY,
    queryFn: async () => {
      const response = await Get(SETTINGS.getFeePolicy, token);
      return response?.data?.feePolicy || null;
    },
    enabled: !!token,
  });
};

export const useUpdateFeePolicy = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (body) => {
      const response = await Post(SETTINGS.updateFeePolicy, body, token);
      if (response?.data?.status === false) {
        throw new Error(response?.data?.message || "Could not save fee policy");
      }
      return response?.data?.data?.feePolicy || body;
    },
    onSuccess: (feePolicy) => {
      queryClient.setQueryData(FEE_POLICY_KEY, feePolicy);
    },
  });
};
